import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import VmiPageTypes from "@insite/client-framework/Types/VmiPageTypes";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@insite/mobius/Breadcrumbs";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { LinkProps } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import React, { FC, useRef } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
}

interface OwnProps {
    extendedStyles?: PageBreadcrumbsStyles;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumbs: {
        css: css`
            width: 100%;
            display: flex;
            a {
                span {
                    &:first-letter {
                        text-transform: capitalize;
                    }
                }
            }
            .disable-breadcrumb {
                pointer-events: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            padding-bottom: 10px;
                            overflow-x: auto;
                            white-space: nowrap;
                        `,
                    ],
                    "max",
                )}

            span[aria-current="page"] {
                color: #2450a8;
                font-weight: 600;
            }

            /* Mobile Breadcrumb for all pages */
            &.mobile-breadcrumb {
                width: calc(100% - 160px);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                // display: flex;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 100%;
                                a {
                                    display: none;
                                    &:last-of-type {
                                        display: inline-flex;
                                    }

                                    &:before {
                                        content: "<";
                                        color: #2450a8;
                                        padding-right: 10px;
                                        font-size: 18px;
                                    }
                                }

                                > span {
                                    display: none;
                                }
                            `,
                            null,
                            css`
                                padding-bottom: 10px;
                                overflow-x: auto;
                                white-space: nowrap;
                                text-overflow: inherit;
                            `,
                        ],
                        "max",
                    )}
                span[aria-current="page"] {
                    color: #2450a8;
                    font-weight: 600;
                }
            }
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const homePageLink = { children: translate("Home"), href: "/" };

const PageBreadcrumbs: FC<Props> = (props: Props) => {
    let links = props.links || generateLinksFrom(props.linksState, props.nodeId);
    const mounted = useRef<boolean>();
    if (links.length > 0 && links[0].href !== homePageLink.href) {
        links = [homePageLink, ...links];
    }

    React.useEffect(() => {
        if (typeof window !== "undefined") {
            const selectStateEl: HTMLSelectElement | null = document.querySelector(
                '[data-test-selector="pageBreadcrumbs"]',
            );
            if (selectStateEl) {
                selectStateEl.classList.add("mobile-breadcrumb");
            }
            // Do componentDidMount logic
            if (!mounted.current) {
                window.onpopstate = () => {
                    window.location.reload();
                };
                mounted.current = true;
                disableAndAddBreadcrumbFunction();
            } else {
                // Do componentDidUpdate logic
                disableAndAddBreadcrumbFunction();
            }
        }
    });

    // Disabled Service, Expertise and Resources from breadcrum on change of navigation and add class
    const disableAndAddBreadcrumbFunction = () => {
        const disableBreadcrumb: HTMLSelectElement | null = document.querySelector(
            '[data-test-selector="pageBreadcrumbs"] a:nth-of-type(2)',
        );
        const selectStateEl: HTMLSelectElement | null = document.querySelector(
            '[data-test-selector="pageBreadcrumbs"]',
        );
        if (disableBreadcrumb) {
            if (
                disableBreadcrumb.textContent === "Services" ||
                disableBreadcrumb.textContent === "Expertise" ||
                disableBreadcrumb.textContent === "Resources"
            ) {
                disableBreadcrumb.classList.add("disable-breadcrumb");
            } else {
                disableBreadcrumb.classList.remove("disable-breadcrumb");
            }
        }
    };
    const styles = useMergeStyles("pageBreadcrumbs", pageBreadcrumbStyles, props.extendedStyles);

    return <Breadcrumbs links={links} data-test-selector="pageBreadcrumbs" {...styles.breadcrumbs} />;
};

export function generateLinksFrom(linksState: LinksState, nodeId: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);
    const vmiExcludePageTypes: string[] = ["HomePage", "MyAccountPage"];
    const search = (targetText: string) => (sourceTextArray: string) => sourceTextArray === targetText;
    const isVmiPageType = !currentLink?.type ? false : VmiPageTypes.some(search(currentLink.type));
    while (currentLink) {
        const isVmiExcludePageType = !currentLink?.type ? false : vmiExcludePageTypes.some(search(currentLink.type));
        if (!isVmiPageType || !isVmiExcludePageType) {
            links.unshift({
                children: currentLink.type === "HomePage" ? translate("Home_Breadcrumb") : currentLink.title,
                href: currentLink.url,
                type: currentLink.type,
            });
        }
        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
